import React, { ReactNode } from 'react';
import App from 'next/app';
import NextAppRouter from 'next/router';
import { BrowserMetrics, WebVital } from '@spotify-internal/semantic-metrics';
import { KeyboardDetectionProvider } from '@spotify-internal/encore-web';
import { RemoteConfigProvider } from '@spotify-internal/remote-config-integration-react';
import { Configuration } from '@spotify-internal/remote-config-properties';
import { captureException } from '@sentry/nextjs';

import { Header } from 'components/Header/Header';
import { Footer } from 'components/Footer/Footer';
import { sendMetric, withRouteMetrics } from 'utils/metrics';
import analytics from 'utils/client/analytics/analytics';
import MarketProvider from 'providers/market';
import MessagingProviderWrapper from 'providers/messaging/MessagingProviderWrapper';
import { TranslationProvider } from 'providers/translations/TranslationProvider';
import '@spotify-internal/masthead-react/index.css';
import '@spotify-internal/encore-web/css/encore-dark-theme.css';
import '@spotify-internal/encore-web/css/encore-creator-light-theme.css';
import '@spotify-internal/encore-web/css/encore-s4p-light-theme.css';
import '@spotify-internal/encore-web/css/fonts.css';
import { SharedPageData } from 'utils/shared-page-data';
import UbiLoggerProvider from 'utils/ubi/ubiProvider';
import { VerticalProvider } from 'providers/vertical';
import { Layout, MainContainer } from 'components/Layout';
import { getVerticalConfig } from 'utils/vertical';
import MastheadProvider from 'providers/MastheadProvider';
import S4AMastheadProviderWrapper from 'providers/S4AMastheadProviderWrapper';

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  const ReactDOM = require('react-dom');
  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000);
}

export class CustomApp extends App<{ pageProps: { sharedPageData: SharedPageData; Component: ReactNode } }> {
  componentDidCatch(error: Error) {
    captureException(error);
  }

  async componentDidMount() {
    const { pageProps } = this.props;
    // No sharedPageData? We're on the error component
    if (!pageProps.sharedPageData) return;

    const { market, usePreview } = pageProps.sharedPageData;

    // Preview page should not show cookie bar. As a consequence, no tracking for these visits.
    if (!usePreview) {
      analytics.initialize(market.marketCode);
    }
  }

  render() {
    const { Component, pageProps } = this.props;

    // No sharedPageData? We're on the error component
    if (!pageProps.sharedPageData) {
      return <Component {...pageProps} />;
    }

    const { remoteConfigJSON, market, translations, isPlain, isLoggedIn, vertical } =
      pageProps.sharedPageData as SharedPageData;

    const verticalConfig = getVerticalConfig(vertical);

    const configuration = remoteConfigJSON ? Configuration.fromJSON(remoteConfigJSON) : null;
    return (
      <Layout theme={verticalConfig.theme}>
        <UbiLoggerProvider isLoggedIn={isLoggedIn}>
          <RemoteConfigProvider configuration={configuration}>
            <KeyboardDetectionProvider>
              <MarketProvider market={market}>
                <TranslationProvider translations={translations}>
                  <VerticalProvider value={verticalConfig}>
                    <MessagingProviderWrapper locale={market.locale} marketCode={market.marketCode}>
                      <MastheadProvider isPlain={isPlain}>
                        <S4AMastheadProviderWrapper>
                          <Header />
                          <MainContainer>
                            <Component {...pageProps} isPlain={isPlain} />
                          </MainContainer>
                          <Footer />
                        </S4AMastheadProviderWrapper>
                      </MastheadProvider>
                    </MessagingProviderWrapper>
                  </VerticalProvider>
                </TranslationProvider>
              </MarketProvider>
            </KeyboardDetectionProvider>
          </RemoteConfigProvider>
        </UbiLoggerProvider>
      </Layout>
    );
  }
}

export function reportWebVitals(metric: WebVital) {
  sendMetric(
    BrowserMetrics.getWebVitalsMetric(metric, {
      route: NextAppRouter.router?.route ?? 'unknown',
    }),
  );
}

export default withRouteMetrics(CustomApp as any);
